/**
 * useFocusTrap — the sign-in overlay's focus trap, extracted so the column and the sheet get the
 * same one (re-review N8: Enter on a tile's picture opened the column, but focus stayed on the tile
 * behind the scrim and Tab walked the dimmed wall). The behaviour is the shipped sign-in trap's,
 * unchanged: on activation focus moves into the layer, Tab and Shift+Tab cycle inside it — a
 * document listener in the CAPTURE phase, so focus that has escaped the layer is pulled back rather
 * than merely wrapped — Escape closes it, and on deactivation focus returns to whatever opened it.
 *
 * `preventScroll` on the restore is the one addition the extraction needed: the column also closes
 * on scroll (BRIEF §6, the fold into the pill), and a plain `focus()` back onto the tile that opened
 * it would scroll the wall back to where the user just left.
 */
import {useEffect, useRef, type RefObject} from 'react'

const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'

export type FocusTrapOptions = {
    /** The layer is on screen AND owns the keyboard. A column under the sign-in card passes false:
     *  one layer traps at a time, and the innermost one is the one the user is answering. */
    active: boolean
    containerRef: RefObject<HTMLElement | null>
    onEscape: () => void
    /** Where focus lands on open when the first focusable element is not the right door — the
     *  phone sheet points here at its heading, so opening it never summons the keyboard unbidden. */
    initialFocusRef?: RefObject<HTMLElement | null>
}

export function useFocusTrap({active, containerRef, onEscape, initialFocusRef}: FocusTrapOptions) {
    // The caller's handler is almost always an inline arrow; holding it in a ref keeps the effect
    // keyed on `active` alone, so a parent re-render never re-runs the trap and re-steals focus.
    const escapeRef = useRef(onEscape)
    escapeRef.current = onEscape
    /** Code-quality review F14: the sign-in overlay deactivates a Column/Sheet trap without
     *  unmounting it (`trapFocus={!signInOpen}`) — only the prop changes, the layer stays mounted
     *  underneath. Re-capturing `document.activeElement` on THAT reactivation used to read whatever
     *  was focused during the interruption (the sign-in dialog itself, about to unmount), so the
     *  restore on the layer's later, genuine close called `focus()` on a detached node and lost the
     *  return to the tile that opened it. Captured only once per value here — kept across the
     *  transient churn as long as it is still connected — and forgotten only once the CONTAINER
     *  itself leaves the DOM (a real unmount, as the sign-in overlay's own trap does on every
     *  close), so a genuinely new open still captures fresh. */
    const restoreToRef = useRef<HTMLElement | null>(null)

    useEffect(() => {
        if (!active) return
        const node = containerRef.current
        if (restoreToRef.current === null || !restoreToRef.current.isConnected) {
            restoreToRef.current = document.activeElement as HTMLElement | null
        }
        const focusables = () =>
            node
                ? Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
                      (el) => el.offsetParent !== null,
                  )
                : []
        // `preventScroll`: the layers this trap serves are overlays, but the sign-in layer's wrapper
        // sits in flow as the page's last child (`Wall.tsx`), and a bare `focus()` on it scrolled
        // the whole wall to its end under the card (operator-observed on Ovid dev, 2026-09-18).
        ;(initialFocusRef?.current ?? focusables()[0])?.focus({preventScroll: true})

        function onKey(e: KeyboardEvent) {
            if (e.key === 'Escape') {
                e.stopPropagation()
                escapeRef.current()
                return
            }
            if (e.key !== 'Tab') return
            const items = focusables()
            if (items.length === 0) return
            const first = items[0]
            const last = items[items.length - 1]
            const activeEl = document.activeElement
            const inside = node?.contains(activeEl as Node | null)
            if (e.shiftKey && (activeEl === first || !inside)) {
                e.preventDefault()
                last.focus()
            } else if (!e.shiftKey && (activeEl === last || !inside)) {
                e.preventDefault()
                first.focus()
            }
        }
        document.addEventListener('keydown', onKey, true)
        return () => {
            document.removeEventListener('keydown', onKey, true)
            restoreToRef.current?.focus?.({preventScroll: true})
            // Only forget the captured target once the container itself has left the DOM — a layer
            // that stays mounted but merely yields the trap (Column/Sheet under the sign-in card)
            // must still return focus to what opened IT on its own later close, not to whatever was
            // focused during the interruption.
            if (!node?.isConnected) restoreToRef.current = null
        }
    }, [active, containerRef, initialFocusRef])
}
